import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Armchair,
  Loader2,
  ExternalLink,
  ArrowRight,
  Info,
  Trash2,
  MapPinned,
} from "lucide-react";
import { toast } from "sonner";
import { venuesApi } from "../lib/events";
import { extractError } from "../lib/api";

const SEATED_TYPES = new Set(["reserved_seating", "table_seating"]);

/**
 * Sprint AJ+ / seating-hotfix — organiser-facing seating picker.
 *
 * Adds the missing UI that lets an organiser attach an existing published
 * seated venue template to their event. The attach/detach endpoints (in
 * venues.py) already exist and are used by demo seeds — this component just
 * makes them discoverable from the EventForm.
 *
 * Props:
 *   eventId          — string | null (null in create mode; whole section is
 *                      shown as "Save first" hint)
 *   event            — the loaded event doc (needed for seating_mode +
 *                      ticket_types_summary for the sold-guardrail)
 *   onChanged()      — called after a successful attach / detach so the
 *                      parent can refetch the event
 */
export default function SeatingArrangementSection({ eventId, event, onChanged, onSeatingModeChange }) {
  const [templates, setTemplates] = useState(null); // null=loading, []=none, [...]
  const [snapshot, setSnapshot] = useState(null);
  const [pickedId, setPickedId] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [confirmDetach, setConfirmDetach] = useState(false);

  const isEdit = !!eventId;
  // Snapshot presence is the source of truth for "attached" state on the
  // organiser side, because the /api/events/{id} endpoint does not enrich
  // `seating_mode` (only public endpoints do). We also fall back to
  // event.seating_mode when it happens to be present.
  const snapshotTemplateType =
    snapshot?.template_type || snapshot?.snapshot_data?.template?.template_type;
  const seatingMode =
    (snapshotTemplateType && SEATED_TYPES.has(snapshotTemplateType)
      ? snapshotTemplateType
      : event?.seating_mode) || "general_admission";
  const isSeated = SEATED_TYPES.has(seatingMode);
  const soldCount =
    event?.ticket_types_summary?.total_sold ??
    (event?.ticket_types || []).reduce((s, t) => s + (t?.sold || 0), 0);
  const lockedBySales = soldCount > 0;

  const [mode, setMode] = useState(isSeated ? "reserved_seating" : "general_admission");

  useEffect(() => {
    setMode(isSeated ? "reserved_seating" : "general_admission");
  }, [isSeated]);

  // Fetch venues + their published seated templates in one pass. Uses a
  // small concurrency window because organisers with many venues would
  // otherwise fire 40+ parallel /templates requests and trip the edge WAF.
  const loadTemplates = useCallback(async () => {
    if (!isEdit) return;
    try {
      const venues = await venuesApi.list();
      const results = [];
      const POOL = 3;
      for (let i = 0; i < venues.length; i += POOL) {
        const slice = venues.slice(i, i + POOL);
        // eslint-disable-next-line no-await-in-loop
        const chunk = await Promise.all(
          slice.map(async (v) => {
            try {
              const tpls = await venuesApi.listTemplates(v.id);
              return tpls
                .filter(
                  (t) =>
                    SEATED_TYPES.has(t.template_type) &&
                    t.status === "published",
                )
                .map((t) => ({ ...t, _venue_name: v.name }));
            } catch {
              return [];
            }
          }),
        );
        results.push(...chunk);
      }
      setTemplates(results.flat());
    } catch (e) {
      setTemplates([]);
      toast.error(extractError(e));
    }
  }, [isEdit]);

  // Fetch snapshot always in edit mode — the organiser event endpoint does
  // not enrich seating_mode, so snapshot presence is the only reliable
  // signal that a template is attached.
  const loadSnapshot = useCallback(async () => {
    if (!isEdit) {
      setSnapshot(null);
      return;
    }
    try {
      const snap = await venuesApi.getEventSnapshot(eventId);
      setSnapshot(snap || null);
    } catch {
      setSnapshot(null);
    }
  }, [isEdit, eventId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);
  // Bubble the derived seating mode up to the parent so it can show/hide
  // the ticket-types-seated-hint. The organiser-side event endpoint does
  // not enrich seating_mode, so the parent can't derive this on its own.
  useEffect(() => {
    onSeatingModeChange?.(seatingMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatingMode]);

  const canAttach = useMemo(
    () => isEdit && !isSeated && !lockedBySales && !!pickedId,
    [isEdit, isSeated, lockedBySales, pickedId],
  );

  const handleAttach = async () => {
    if (!canAttach) return;
    setAttaching(true);
    try {
      await venuesApi.attachTemplate(eventId, pickedId);
      toast.success("Seating layout attached");
      setPickedId("");
      await loadSnapshot();
      await onChanged?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async () => {
    if (!isEdit || !isSeated || lockedBySales) return;
    setDetaching(true);
    try {
      await venuesApi.detachTemplate(eventId);
      toast.success("Seating layout removed. Event is now General Admission.");
      setConfirmDetach(false);
      setSnapshot(null);
      await onChanged?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDetaching(false);
    }
  };

  // ------------- Render -------------
  return (
    <div className="gt-card" data-testid="event-seating-section">
      <div className="gt-label mb-4">02b · Seating arrangement</div>

      {!isEdit && (
        <div
          className="flex items-start gap-3 p-3 rounded-sm bg-[var(--gt-bg-surface)] border border-[var(--gt-line)]"
          data-testid="seating-create-first-hint"
        >
          <Info size={16} className="text-[var(--gt-ink-2)] shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--gt-ink-2)]">
            Save this event first, then return here to attach a reserved seating
            layout. General Admission is the default until you attach one.
          </p>
        </div>
      )}

      {isEdit && (
        <>
          {lockedBySales && (
            <div
              className="mb-4 flex items-start gap-3 p-3 rounded-sm bg-[var(--gt-bg-surface)] border border-[var(--gt-line)]"
              data-testid="seating-locked-notice"
            >
              <Info size={16} className="text-[var(--gt-ink-2)] shrink-0 mt-0.5" />
              <p className="text-sm text-[var(--gt-ink-2)]">
                Seating layout cannot be changed after tickets have been sold.
              </p>
            </div>
          )}

          {/* Mode selector */}
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
            data-testid="seating-mode-group"
          >
            <ModeCard
              testid="seating-mode-ga"
              active={mode === "general_admission"}
              disabled={lockedBySales || attaching || detaching}
              icon={<Armchair size={18} />}
              title="General admission"
              desc="Ticket quantities control capacity. Buyers pick a ticket type, not a specific seat."
              onClick={() => {
                setMode("general_admission");
                if (isSeated) setConfirmDetach(true);
              }}
            />
            <ModeCard
              testid="seating-mode-reserved"
              active={mode === "reserved_seating"}
              disabled={lockedBySales || attaching || detaching}
              icon={<MapPinned size={18} />}
              title="Reserved seating"
              desc="Attach a saved seating layout from your venues so buyers pick a specific seat."
              onClick={() => setMode("reserved_seating")}
            />
            <Link
              to="/dashboard/venues"
              className="p-4 border border-dashed border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] transition-colors block"
              data-testid="seating-create-link"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-[var(--gt-ink-2)]">
                  <ExternalLink size={16} />
                </span>
              </div>
              <div className="font-semibold text-sm text-[var(--gt-ink)]">
                Create seating layout
              </div>
              <p className="text-xs text-[var(--gt-ink-2)] mt-1">
                Create a venue layout first, then return here to attach it.
              </p>
            </Link>
          </div>

          {/* Attached state */}
          {isSeated && snapshot && (
            <div
              className="mt-5 p-4 border border-[var(--gt-ink)] rounded-sm bg-[var(--gt-bg-surface)]"
              data-testid="seating-attached-card"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">
                    Seating attached
                  </div>
                  <div
                    className="font-heading text-lg font-black tracking-tight leading-tight"
                    data-testid="seating-attached-name"
                  >
                    {snapshot?.snapshot_data?.template?.template_name ||
                      snapshot?.source_template_name ||
                      "Seating layout"}
                  </div>
                  <div className="text-xs text-[var(--gt-ink-2)] mt-1">
                    {snapshot?.snapshot_data?.venue?.name ? (
                      <>at {snapshot.snapshot_data.venue.name} · </>
                    ) : null}
                    {snapshot?.snapshot_data?.template?.capacity ||
                      snapshot?.capacity ||
                      "—"}{" "}
                    seats · Render mode:{" "}
                    {snapshot?.snapshot_data?.template?.render_mode ||
                      snapshot?.render_mode ||
                      "list"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!lockedBySales && (
                    <button
                      type="button"
                      onClick={() => setConfirmDetach(true)}
                      className="gt-btn-ghost text-xs"
                      data-testid="seating-detach-btn"
                      aria-label="Remove seating layout"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Picker (only when reserved selected & nothing attached yet) */}
          {mode === "reserved_seating" && !isSeated && (
            <div className="mt-5" data-testid="seating-picker-block">
              {templates === null && (
                <div className="flex items-center gap-2 text-[var(--gt-ink-2)] text-sm">
                  <Loader2 size={14} className="animate-spin" /> Loading your
                  seating layouts…
                </div>
              )}
              {Array.isArray(templates) && templates.length === 0 && (
                <div
                  className="p-4 border border-dashed border-[var(--gt-line)] rounded-sm"
                  data-testid="seating-picker-empty"
                >
                  <p className="text-sm text-[var(--gt-ink-2)] mb-3">
                    You don&apos;t have any published seating layouts yet.
                    Create one in your Venues dashboard, publish it, then come
                    back here to attach it.
                  </p>
                  <Link
                    to="/dashboard/venues"
                    className="gt-btn-primary text-sm inline-flex"
                    data-testid="seating-goto-venues"
                  >
                    Go to Venues <ArrowRight size={14} />
                  </Link>
                </div>
              )}
              {Array.isArray(templates) && templates.length > 0 && (
                <>
                  <label className="gt-form-label">Choose a seating layout</label>
                  <select
                    className="gt-input"
                    value={pickedId}
                    onChange={(e) => setPickedId(e.target.value)}
                    disabled={lockedBySales || attaching}
                    data-testid="seating-template-picker"
                  >
                    <option value="">— Select a published layout —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t._venue_name} · {t.template_name} ·{" "}
                        {t.capacity || "—"} seats ·{" "}
                        {t.template_type === "table_seating"
                          ? "Table"
                          : "Reserved"}{" "}
                        · {t.render_mode === "visual" ? "Visual" : "List"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAttach}
                    disabled={!canAttach || attaching}
                    className="gt-btn-primary mt-3 text-sm disabled:opacity-60"
                    data-testid="seating-attach-btn"
                  >
                    {attaching ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    Attach seating layout
                  </button>
                </>
              )}
            </div>
          )}

          {/* Detach confirmation */}
          {confirmDetach && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              data-testid="seating-detach-modal"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-white rounded-sm max-w-md w-full p-6 border border-[var(--gt-line)]">
                <h3 className="font-heading text-xl font-black tracking-tight mb-2">
                  Remove seating layout?
                </h3>
                <p className="text-sm text-[var(--gt-ink-2)] mb-5">
                  This event will switch to General Admission and buyers will
                  no longer pick specific seats. You can attach a different
                  layout afterwards. This action is only allowed because no
                  tickets have been sold yet.
                </p>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDetach(false)}
                    className="gt-btn-ghost text-sm"
                    data-testid="seating-detach-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDetach}
                    disabled={detaching}
                    className="gt-btn-primary text-sm disabled:opacity-60"
                    data-testid="seating-detach-confirm"
                  >
                    {detaching ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                    Yes, switch to GA
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ModeCard({ testid, active, disabled, icon, title, desc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`text-left p-4 border rounded-sm transition-colors ${
        active
          ? "border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white"
          : "border-[var(--gt-line)] hover:border-[var(--gt-ink)]"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <div className={`mb-2 ${active ? "text-white" : "text-[var(--gt-ink-2)]"}`}>
        {icon}
      </div>
      <div className="font-semibold text-sm mb-1">{title}</div>
      <p
        className={`text-xs leading-relaxed ${
          active ? "text-white/80" : "text-[var(--gt-ink-2)]"
        }`}
      >
        {desc}
      </p>
    </button>
  );
}
